import { useEffect, useState } from 'react';
import { Polygon, Polyline, CircleMarker, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// Map-side interactions for the custom-vineyard draw tool. Mounted only
// while a draw session is active; all state lives in useDrawVineyard.
//
// Interaction contract (see the feature spec):
// - draw mode freezes drag-panning entirely; scroll zoom stays live
// - crosshair cursor whenever draw mode is active
// - a click that moved < ~5px still counts as a point; bigger moves don't
// - clicking within the snap radius of the (highlighted) start vertex
//   closes the ring; double-click closes as a fallback
// - right-click / Esc undo the last point, repeatable to zero

const CLICK_SLOP_PX = 5;
const SNAP_PX = 14;
const PURPLE = '#6d5aad';

const deleteIcon = L.divIcon({
  className: 'draw-chunk-delete',
  html: '×',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function chunkCenter(points) {
  let lat = 0, lng = 0;
  for (const [a, b] of points) { lat += a; lng += b; }
  return [lat / points.length, lng / points.length];
}

export default function DrawLayer({ draw }) {
  const { session, addPoint, undoPoint, closeChunk, deleteChunk } = draw;
  const map = useMap();
  const [nearStart, setNearStart] = useState(false);
  const [downAt, setDownAt] = useState(null);

  const drawing = session.mode === 'draw' && session.stage === 'draw';
  const open = session.open;

  // For the whole session, existing polygons must not react to clicks (no
  // selection impression) — pointer events fall through to the map, which
  // is what places points. See .map--draw-session in styles.css.
  useEffect(() => {
    const container = map.getContainer();
    container.classList.add('map--draw-session');
    return () => container.classList.remove('map--draw-session');
  }, [map]);

  // Freeze panning + crosshair in draw mode; grab mode restores panning.
  // Scroll zoom is never touched. Everything restores on unmount.
  useEffect(() => {
    const container = map.getContainer();
    if (session.mode === 'draw') {
      map.dragging.disable();
      map.doubleClickZoom.disable();
      container.classList.add('map--drawing');
    } else {
      map.dragging.enable();
      map.doubleClickZoom.disable(); // dblclick stays "close ring" all session
      container.classList.remove('map--drawing');
    }
    return () => {
      map.dragging.enable();
      map.doubleClickZoom.enable();
      container.classList.remove('map--drawing');
    };
  }, [map, session.mode]);

  // Esc = undo last point (repeatable).
  useEffect(() => {
    if (!drawing) return;
    const onKey = (e) => { if (e.key === 'Escape') undoPoint(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawing, undoPoint]);

  const startPointPx = () => open.length > 0
    ? map.latLngToContainerPoint(L.latLng(open[0][0], open[0][1]))
    : null;

  useMapEvents({
    mousedown: (e) => setDownAt(e.containerPoint),
    mousemove: (e) => {
      if (!drawing || open.length < 3) { if (nearStart) setNearStart(false); return; }
      const sp = startPointPx();
      setNearStart(!!sp && e.containerPoint.distanceTo(sp) <= SNAP_PX);
    },
    click: (e) => {
      if (!drawing) return;
      // moved more than the slop: a (frozen) pan attempt, not a point
      if (downAt && e.containerPoint.distanceTo(downAt) > CLICK_SLOP_PX) return;
      // snap-close on the start vertex
      const sp = startPointPx();
      if (open.length >= 3 && sp && e.containerPoint.distanceTo(sp) <= SNAP_PX) {
        closeChunk();
        setNearStart(false);
        return;
      }
      // ignore the duplicate point of a double-click's second click
      if (open.length > 0) {
        const lp = map.latLngToContainerPoint(L.latLng(open[open.length - 1][0], open[open.length - 1][1]));
        if (e.containerPoint.distanceTo(lp) <= 2) return;
      }
      addPoint([e.latlng.lat, e.latlng.lng]);
    },
    dblclick: (e) => {
      L.DomEvent.stop(e.originalEvent);
      if (drawing && open.length >= 3) { closeChunk(); setNearStart(false); }
    },
    contextmenu: (e) => {
      e.originalEvent.preventDefault();
      if (drawing) undoPoint();
    },
  });

  return (
    <>
      {session.chunks.map(chunk => (
        <Polygon
          key={chunk.id}
          positions={chunk.points}
          pathOptions={{ color: PURPLE, weight: 2, fillColor: PURPLE, fillOpacity: 0.25 }}
        />
      ))}
      {session.chunks.map(chunk => (
        <Marker
          key={`del-${chunk.id}`}
          position={chunkCenter(chunk.points)}
          icon={deleteIcon}
          eventHandlers={{ click: () => deleteChunk(chunk.id) }}
        />
      ))}
      {open.length > 1 && (
        <Polyline positions={open} pathOptions={{ color: PURPLE, weight: 2, dashArray: '5 4' }} />
      )}
      {open.map(([lat, lng], i) => (
        <CircleMarker
          key={`v-${i}`}
          center={[lat, lng]}
          radius={i === 0 && nearStart ? 8 : i === 0 ? 5.5 : 4}
          pathOptions={{
            color: PURPLE,
            weight: 2,
            fillColor: i === 0 && nearStart ? PURPLE : '#fff',
            fillOpacity: 1,
            bubblingMouseEvents: i !== 0,
          }}
          eventHandlers={i === 0 ? {
            click: (e) => {
              L.DomEvent.stop(e.originalEvent);
              if (drawing && open.length >= 3) closeChunk();
              else if (drawing) addPoint([lat, lng]);
            },
          } : undefined}
        />
      ))}
    </>
  );
}
